import type React from "react";

import type { OpenAPI } from "simplydo/interfaces";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { FormField } from "simplydo/react/ui/form";
import { Input } from "simplydo/react/ui/input";

import type {
  IdeaCustomFieldIdea,
  IdeaWithIdeaTemplate,
} from "components/lib/Ideas/IdeaPreview/types";

import { getStringFieldOptions } from "./FieldProps";

type TemplateField = NonNullable<
  NonNullable<OpenAPI.Schemas["IdeaTemplate"]["body"]>
>[number]["fields"][number];

type VisibilityConditionsType = NonNullable<
  TemplateField["visibilityConditions"]
>;
type VisibilityCondition = NonNullable<
  VisibilityConditionsType["conditions"]
>[number];

const isNotEmpty = (field: string | undefined) =>
  field !== null && field !== undefined && field !== "";

const isEmpty = (field: string | undefined) => !isNotEmpty(field);

const equalTo = (field: string | undefined, value: string) => field === value;

const notEqualTo = (field: string | undefined, value: string) =>
  field && value && field !== value;

/**
 * This controls which checks are available for any field type.
 * If modified please also update the corresponding type in the backend. (package: python-utils/simplydo/conditions.py)
 */
export const conditionChecks = {
  shorttext: {
    isEmpty: isEmpty,
    isNotEmpty: isNotEmpty,
    equalTo: equalTo,
    notEqualTo: notEqualTo,
  },
  longtext: {
    isEmpty: isEmpty,
    isNotEmpty: isNotEmpty,
    equalTo: equalTo,
    notEqualTo: notEqualTo,
  },
  numerictext: {
    isEmpty: isEmpty,
    isNotEmpty: isNotEmpty,
    equalTo: (field: string | undefined, value: string) =>
      field && value && equalTo(field.toString(), value.toString()),
    notEqualTo: (field: string | undefined, value: string) =>
      field && value && notEqualTo(field.toString(), value.toString()),
  },
  richtext: {
    isEmpty: (field: string | undefined) =>
      isEmpty(field) || field === "<p></p>",
    isNotEmpty: (field: string | undefined) =>
      isNotEmpty(field) && field !== "<p></p>",
  },
  slider: {
    equalTo: (field: { value: number | undefined }, value: string) =>
      field?.value && equalTo(field.value.toString(), value),
    notEqualTo: (field: { value: number | undefined }, value: string) =>
      field?.value && notEqualTo(field.value.toString(), value),
  },
  dropdown: {
    isNotEmpty: isNotEmpty,
    isEmpty: isEmpty,
    equalTo: equalTo,
    notEqualTo: notEqualTo,
  },
  optionMultiple: {
    includes: (field: string[] | undefined, value: string) =>
      field?.includes(value),
    doesNotInclude: (field: string[] | undefined, value: string) =>
      field && !field.includes(value),
    isNotEmpty: (field: string[] | undefined) => field && field.length > 0,
    isEmpty: (field: string[] | undefined) =>
      !field || (field && field.length === 0),
  },
  creditsafereport: {
    isNotEmpty: (field: { _id: string | undefined }) => field?._id,
    isEmpty: (field: { _id: string | undefined }) =>
      !field || (field && !field._id),
  },
} as const;

export type ConditionCheckFieldType = keyof typeof conditionChecks;

export const getDefaultCondition = (fieldType: ConditionCheckFieldType) => {
  const availableChecks = conditionChecks[fieldType];
  if (!availableChecks) {
    return null;
  }
  if ("isNotEmpty" in availableChecks) {
    return "isNotEmpty";
  }
  if ("equalTo" in availableChecks) {
    return "equalTo";
  }
  return null;
};

const transformerLookup = {
  isEmpty: { key: "isEmpty", value: "isEmpty", text: "is empty." },
  isNotEmpty: { key: "isNotEmpty", value: "isNotEmpty", text: "has a value." },
  equalTo: { key: "equalTo", value: "equalTo", text: "is equal to:" },
  notEqualTo: {
    key: "notEqualTo",
    value: "notEqualTo",
    text: "is not equal to:",
  },
  includes: { key: "includes", value: "includes", text: "includes:" },
  doesNotInclude: {
    key: "doesNotInclude",
    value: "doesNotInclude",
    text: "does not include:",
  },
} as const;

export const renderValueCheck = (
  condition: VisibilityCondition,
  field: TemplateField,
  onChange: (value: string) => void,
) => {
  if (!field) {
    return (
      <FormField disabled>
        <Input fluid />
      </FormField>
    );
  }
  switch (condition.condition) {
    case "equalTo":
    case "notEqualTo":
      if (field.type === "dropdown") {
        return (
          <FormField>
            <Dropdown
              fluid
              key={field.id}
              variant="outline"
              value={condition.value}
              options={getStringFieldOptions(field.options).map((o) => ({
                key: o,
                value: o,
                text: o,
              }))}
              onChange={(_e, { value }) => onChange(String(value ?? ""))}
            />
          </FormField>
        );
      }
      return (
        <FormField>
          <Input
            fluid
            key={field.id}
            value={condition.value}
            onChange={(
              _e: React.SyntheticEvent,
              { value }: { value: string },
            ) => onChange(value)}
          />
        </FormField>
      );

    case "includes":
    case "doesNotInclude":
      return (
        <FormField>
          <Dropdown
            key={field.id}
            variant="outline"
            value={condition.value}
            options={getStringFieldOptions(field.options).map((o) => ({
              key: o,
              value: o,
              text: o,
            }))}
            onChange={(_e, { value }) => onChange(String(value ?? ""))}
          />
        </FormField>
      );
    default:
      return (
        <FormField disabled>
          <Input fluid />
        </FormField>
      );
  }
};

export const availableConditions = Object.fromEntries(
  Object.entries(conditionChecks).map(([key, value]) => [
    key,
    Object.keys(value)
      .filter(
        (transformerName) =>
          (transformerName as VisibilityCondition["condition"]) in
          transformerLookup,
      )
      .map(
        (transformerName) =>
          transformerLookup[
            transformerName as VisibilityCondition["condition"]
          ],
      ),
  ]),
);

export const fieldVisibilityCheck = (
  idea: IdeaWithIdeaTemplate | IdeaCustomFieldIdea,
  field: TemplateField,
) => {
  if (!idea.ownerIdeaTemplate) {
    return true;
  }

  const fieldTypes: Record<string, ConditionCheckFieldType> = {};
  for (const section of idea.ownerIdeaTemplate.body || []) {
    for (const field of section.fields || []) {
      if (!field.type || !(field.type in conditionChecks)) {
        continue;
      }
      fieldTypes[field.id] = field.type as ConditionCheckFieldType;
    }
  }

  const visibilityConditions = field.visibilityConditions || {};
  if (!visibilityConditions) {
    return true;
  }
  if (!visibilityConditions.enabled) {
    return true;
  }

  const conditions = visibilityConditions.conditions || [];
  if (conditions.length === 0) {
    return true;
  }

  const checks = [];
  for (const condition of conditions) {
    const conditionField = condition.field;
    const conditionCondition = condition.condition;

    if (!conditionField || !conditionCondition) {
      continue;
    }

    const fieldType = fieldTypes[conditionField];
    if (!fieldType) {
      continue;
    }

    const conditionValue = condition.value;
    const fieldValue = idea?.templated?.[conditionField];
    const conditionTransformers = conditionChecks[fieldType] as
      | Record<string, (fieldValue: any, conditionValue: any) => boolean>
      | undefined;
    if (!conditionTransformers) {
      continue;
    }

    const conditionTransformer = conditionTransformers[conditionCondition];
    if (!conditionTransformer) {
      continue;
    }

    checks.push(conditionTransformer(fieldValue, conditionValue));
  }

  const conditionType = visibilityConditions.conditionType || "any";
  if (conditionType === "all") {
    return checks.every((check) => check);
  } else if (conditionType === "any") {
    return checks.some((check) => check);
  }
  return true;
};
